import React, { useEffect, useState } from 'react';
import { Label, PaddingContainer } from '../styledComponents';
import Dropdown from '@/shared/components/Dropdown';
import useExploreStore from '@/stores/useExploreStore';
import { SessionType } from '@/shared/types/generated';

interface DropdownOption {
  label: string;
  value: string | number;
}

const IN_PERSON_MIN_DURATION = 120; // 2 hours in minutes

const SelectSessionType = () => {
  const { booking, setBooking, selectedStylist } = useExploreStore();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  // Check if stylist is available for in-person sessions
  const stylistAvailableInPerson =
    selectedStylist?.isAvailableInPerson ||
    selectedStylist?.computedInPersonAvailability;

  const sessionTypeOptions: DropdownOption[] = [
    { label: 'Virtual', value: SessionType.Video },
    // Only show in-person option if stylist is available for it
    ...(stylistAvailableInPerson
      ? [{ label: 'In-Person', value: SessionType.InPersonSession }]
      : []),
  ];

  const getSelectedLabel = () => {
    const option = sessionTypeOptions.find(
      (opt) => opt.value === booking.sessionType,
    );
    return option?.label || 'Virtual';
  };

  // Sync sessionType when stylist in-person availability changes
  useEffect(() => {
    if (
      booking.sessionType === SessionType.InPersonSession &&
      !stylistAvailableInPerson
    ) {
      setBooking({
        sessionType: SessionType.Video,
        sessionDuration: 60,
        selectedTime: null,
      });
    }
  }, [stylistAvailableInPerson, booking.sessionType, setBooking]);

  const handleSelect = (option: DropdownOption) => {
    const newSessionType = option.value as SessionType;
    const isInPerson = newSessionType === SessionType.InPersonSession;

    // When switching to in-person, set minimum duration of 2 hours
    // When switching to virtual, set default duration of 60 minutes
    const newDuration = isInPerson ? IN_PERSON_MIN_DURATION : 60;

    setBooking({
      sessionType: newSessionType,
      sessionDuration: newDuration,
      // Reset time selection when session type changes since availability may differ
      selectedTime: null,
    });
  };

  // Only show dropdown if stylist offers in-person sessions (multiple options)
  if (!stylistAvailableInPerson) {
    return null;
  }

  return (
    <>
      <PaddingContainer marginBottom={24}>
        <Label>Select the session type:</Label>
      </PaddingContainer>

      <Dropdown
        eyebrowText="Session Type"
        options={sessionTypeOptions}
        selectedOption={booking.sessionType}
        selectedOptionLabel={getSelectedLabel()}
        onSelect={handleSelect}
        isVisible={isDropdownVisible}
        setIsVisible={setIsDropdownVisible}
      />
    </>
  );
};

export default SelectSessionType;
